/**
 * WebNotificationsPageViewModel - Web Platform Adapter
 *
 * Web-specific implementation for notifications page ViewModel
 * Integrates with React Router and web platform services
 */

import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  createNotificationsPageViewModel,
  createWebPlatformServices,
  WebNotificationsPageService,
  type NotificationsPageViewModel
} from '@restaurant/viewmodels'

/**
 * Create web-specific notifications page ViewModel
 * Uses React Router for navigation and web platform services
 */
export const useWebNotificationsPageViewModel = (): NotificationsPageViewModel => {
  const navigate = useNavigate()
  const location = useLocation()

  // Create web-specific platform services (memoized)
  const platformServices = useMemo(() => {
    return createWebPlatformServices(navigate, location)
  }, [navigate, location])

  // Create web-specific service (memoized to prevent infinite re-renders)
  const notificationsPageService = useMemo(() => {
    return new WebNotificationsPageService()
  }, [])

  // Create and return the platform-agnostic NotificationsPageViewModel
  const NotificationsPageViewModelHook = useMemo(() => {
    return createNotificationsPageViewModel(platformServices, { notificationsPageService })
  }, [platformServices, notificationsPageService])

  return NotificationsPageViewModelHook()
}

export default useWebNotificationsPageViewModel